/**
 * ProposalShareLauncher
 *
 * Abre o modal "Compartilhar proposta" a partir da LISTAGEM de propostas
 * (aba Propostas do negócio).
 *
 * Por que existe: a listagem (`get_deal_proposals`) não devolve os dados de
 * contato/negócio exigidos pelo `ProposalShareDialog`. Este componente carrega
 * os detalhes da proposta (`get_proposal_details`, via useProposalDetails) e
 * monta o mesmo `context` usado hoje no modal de detalhes — sem duplicar
 * nenhuma regra de compartilhamento.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.0.0 - Criação inicial (atalho de compartilhar na listagem)
 */

import { useState } from 'react';
import { buildPublicProposalUrl, useProposalDetails, useProposalPdf } from '@/hooks/useDealProposals';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { ProposalShareDialog } from './ProposalShareDialog';

interface ProposalShareLauncherProps {
  /** Proposta a compartilhar (null = fechado) */
  proposalId: string | null;
  /** Fecha o compartilhamento */
  onClose: () => void;
}

export function ProposalShareLauncher({ proposalId, onClose }: ProposalShareLauncherProps) {
  const { data } = useProposalDetails(proposalId, !!proposalId);
  const { getPdfSignedUrl } = useProposalPdf();
  // Conversa aberta após o envio pelo chat interno
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);

  const proposal = data?.proposal;
  const pdfReady = proposal?.pdf_status === 'ready' && !!proposal?.pdf_storage_path;

  return (
    <>
      {proposal && (
        <ProposalShareDialog
          open={!!proposalId}
          onOpenChange={(open) => !open && onClose()}
          getPdfSignedUrl={getPdfSignedUrl}
          onOpenConversation={(conversationId) => setChatConversationId(conversationId)}
          context={{
            proposalId: proposal.id,
            proposalNumber: proposal.proposal_number,
            total: proposal.total,
            currency: proposal.currency,
            expiresAt: proposal.expires_at,
            dealTitle: data?.context?.deal_title ?? null,
            contactId: data?.context?.contact_id ?? null,
            contactName: data?.context?.contact_name ?? null,
            contactPhone: data?.context?.contact_phone ?? null,
            // Link público só quando ativo (mesma regra do modal de detalhes)
            publicUrl:
              proposal.public_enabled && proposal.public_token
                ? buildPublicProposalUrl(proposal.public_token)
                : null,
            pdfStoragePath: pdfReady ? proposal.pdf_storage_path : null,
          }}
        />
      )}

      {/* Chat interno aberto a partir do compartilhamento (mensagem já em rascunho) */}
      <ChatConversationModal
        conversationId={chatConversationId}
        open={!!chatConversationId}
        onOpenChange={(open) => !open && setChatConversationId(null)}
      />
    </>
  );
}
